import type {
  CanonicalPoint,
  ImportedGeometry,
  ProjectDocument,
} from "./types";

const SCRIPT_ID = "flow-wizard-google-maps-script";
const SCRIPT_ENDPOINT = "https://maps.googleapis.com/maps/api/js";
let activeMapsScriptLoad: Promise<void> | null = null;

interface MapLatLngLiteral {
  lat: number;
  lng: number;
}

interface MapLatLng {
  lat(): number;
  lng(): number;
}

interface MapEvent {
  latLng?: MapLatLng | null;
}

interface MapListener {
  remove(): void;
}

interface MapBoundsInstance {
  extend(point: MapLatLngLiteral): void;
}

interface MapInstance {
  addListener(
    eventName: "click",
    handler: (event: MapEvent) => void,
  ): MapListener;
  fitBounds(bounds: MapBoundsInstance, padding?: number): void;
}

interface DataFeature {
  getProperty(name: string): unknown;
}

interface DataStyle {
  clickable?: boolean;
  fillColor?: string;
  fillOpacity?: number;
  icon?: {
    fillColor: string;
    fillOpacity: number;
    path: number;
    scale: number;
    strokeColor: string;
    strokeWeight: number;
  };
  strokeColor?: string;
  strokeOpacity?: number;
  strokeWeight?: number;
  title?: string;
  visible?: boolean;
  zIndex?: number;
}

interface DataLayerInstance {
  addGeoJson(collection: MapFeatureCollection): unknown[];
  forEach(callback: (feature: unknown) => void): void;
  remove(feature: unknown): void;
  setMap(map: MapInstance | null): void;
  setStyle(style: (feature: DataFeature) => DataStyle): void;
}

interface MapsNamespace {
  Data: new (options: {
    controls: never[];
    drawingMode: null;
    map: MapInstance;
  }) => DataLayerInstance;
  LatLngBounds: new () => MapBoundsInstance;
  Map: new (
    element: HTMLElement,
    options: {
      center: MapLatLngLiteral;
      fullscreenControl: boolean;
      mapTypeControl: boolean;
      mapTypeControlOptions: { mapTypeIds: string[] };
      mapTypeId: "terrain";
      streetViewControl: boolean;
      zoom: number;
    },
  ) => MapInstance;
  SymbolPath: { CIRCLE: number };
}

type GlobalWithMaps = typeof globalThis & {
  google?: { maps?: MapsNamespace };
};

type MapGeometry =
  | { coordinates: [number, number]; type: "Point" }
  | { coordinates: [number, number][]; type: "LineString" }
  | { coordinates: [number, number][][]; type: "Polygon" };

interface MapFeature {
  geometry: MapGeometry;
  id: string;
  properties: {
    category: "link" | "node" | "overlay" | "site";
    name: string;
    role: string;
  };
  type: "Feature";
}

export interface MapFeatureCollection {
  features: MapFeature[];
  type: "FeatureCollection";
}

export interface GoogleNetworkMapOptions {
  onMapClick?: (point: CanonicalPoint) => void;
  zoom?: number;
}

function mapsNamespace(): MapsNamespace | null {
  return (globalThis as GlobalWithMaps).google?.maps ?? null;
}

export function toMapLatLng(point: CanonicalPoint): MapLatLngLiteral {
  return { lat: point.latitude_deg, lng: point.longitude_deg };
}

export function fromMapLatLng(point: MapLatLngLiteral): CanonicalPoint {
  return { latitude_deg: point.lat, longitude_deg: point.lng };
}

function coordinate(point: CanonicalPoint): [number, number] {
  return [point.longitude_deg, point.latitude_deg];
}

function importedGeometry(geometry: ImportedGeometry): MapGeometry {
  if (geometry.type === "Point") {
    return { coordinates: coordinate(geometry.coordinates), type: "Point" };
  }
  if (geometry.type === "LineString") {
    return {
      coordinates: geometry.coordinates.map(coordinate),
      type: "LineString",
    };
  }
  return {
    coordinates: geometry.coordinates.map((ring) => ring.map(coordinate)),
    type: "Polygon",
  };
}

export function projectToMapGeoJson(
  document: ProjectDocument,
): MapFeatureCollection {
  const features: MapFeature[] = [];
  for (const link of document.links) {
    features.push({
      geometry: { coordinates: link.path.map(coordinate), type: "LineString" },
      id: `link:${link.id}`,
      properties: { category: "link", name: link.name, role: "hose" },
      type: "Feature",
    });
  }
  for (const overlay of document.overlays ?? []) {
    features.push({
      geometry: importedGeometry(overlay.geometry),
      id: `overlay:${overlay.id}`,
      properties: {
        category: "overlay",
        name: overlay.name,
        role: overlay.role,
      },
      type: "Feature",
    });
  }
  for (const node of document.nodes) {
    features.push({
      geometry: { coordinates: coordinate(node.location), type: "Point" },
      id: `node:${node.id}`,
      properties: { category: "node", name: node.name, role: node.node_type },
      type: "Feature",
    });
  }
  for (const site of document.candidate_pump_sites ?? []) {
    features.push({
      geometry: { coordinates: coordinate(site.location), type: "Point" },
      id: `site:${site.id}`,
      properties: {
        category: "site",
        name: site.name,
        role: "approved_pump_site",
      },
      type: "Feature",
    });
  }
  return { features, type: "FeatureCollection" };
}

function pointsInGeometry(geometry: MapGeometry): Array<[number, number]> {
  if (geometry.type === "Point") return [geometry.coordinates];
  if (geometry.type === "LineString") return geometry.coordinates;
  return geometry.coordinates.flat();
}

function styleForFeature(maps: MapsNamespace, feature: DataFeature): DataStyle {
  const category = String(feature.getProperty("category"));
  const role = String(feature.getProperty("role"));
  const title = String(feature.getProperty("name"));
  if (category === "site") {
    return {
      icon: {
        fillColor: "#f59e0b",
        fillOpacity: 1,
        path: maps.SymbolPath.CIRCLE,
        scale: 9,
        strokeColor: "#7c2d12",
        strokeWeight: 2,
      },
      title,
      zIndex: 40,
    };
  }
  if (category === "node") {
    const fillColor =
      role === "source" ? "#15803d" : role === "sink" ? "#1d4ed8" : "#475569";
    return {
      icon: {
        fillColor,
        fillOpacity: 1,
        path: maps.SymbolPath.CIRCLE,
        scale: role === "junction" ? 6 : 8,
        strokeColor: "#ffffff",
        strokeWeight: 2,
      },
      title: `${title} · ${role}`,
      zIndex: 50,
    };
  }
  if (category === "link") {
    return {
      strokeColor: "#0369a1",
      strokeOpacity: 1,
      strokeWeight: 6,
      title,
      zIndex: 30,
    };
  }
  if (role === "exclusion_area") {
    return {
      fillColor: "#dc2626",
      fillOpacity: 0.2,
      strokeColor: "#b91c1c",
      strokeOpacity: 0.9,
      strokeWeight: 2,
      title: `${title} · exclusion area`,
      zIndex: 10,
    };
  }
  if (role === "permit_area") {
    return {
      fillColor: "#16a34a",
      fillOpacity: 0.16,
      strokeColor: "#15803d",
      strokeOpacity: 0.9,
      strokeWeight: 2,
      title: `${title} · permit area`,
      zIndex: 10,
    };
  }
  return {
    fillColor: "#64748b",
    fillOpacity: 0.12,
    strokeColor: role === "permitted_route" ? "#15803d" : "#475569",
    strokeOpacity: 1,
    strokeWeight: role === "permitted_route" ? 5 : 4,
    title: `${title} · ${role.replaceAll("_", " ")}`,
    zIndex: 20,
  };
}

export async function loadGoogleMapsScript(apiKey: string): Promise<void> {
  const trimmedKey = apiKey.trim();
  if (trimmedKey.length === 0) {
    throw new Error("Enter a user-supplied Maps JavaScript API key.");
  }
  if (mapsNamespace() !== null) {
    return;
  }
  if (activeMapsScriptLoad !== null) {
    await activeMapsScriptLoad;
    return;
  }

  document.getElementById(SCRIPT_ID)?.remove();
  const url = new URL(SCRIPT_ENDPOINT);
  url.searchParams.set("key", trimmedKey);
  url.searchParams.set("v", "weekly");
  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.async = true;
  script.defer = true;
  script.referrerPolicy = "strict-origin-when-cross-origin";
  script.src = url.toString();
  const documentNonce =
    document.querySelector<HTMLScriptElement>("script[nonce]")?.nonce;
  if (documentNonce !== undefined && documentNonce.length > 0) {
    script.nonce = documentNonce;
  }

  const attempt = new Promise<void>((resolve, reject) => {
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error("The map script could not be loaded.")),
      { once: true },
    );
    document.head.append(script);
  });
  const guardedAttempt = attempt
    .then(() => {
      if (mapsNamespace() === null) {
        throw new Error(
          "The map script loaded without a usable Maps namespace.",
        );
      }
      script.remove();
    })
    .catch((reason: unknown) => {
      script.remove();
      throw reason;
    })
    .finally(() => {
      activeMapsScriptLoad = null;
    });
  activeMapsScriptLoad = guardedAttempt;
  await guardedAttempt;
}

export class GoogleNetworkMapAdapter {
  private readonly clickListener: MapListener | null;
  private readonly dataLayer: DataLayerInstance;
  private readonly routeCasing: DataLayerInstance;
  private readonly map: MapInstance;
  private readonly maps: MapsNamespace;
  private projectId: string | null = null;
  private hasFittedGeometry = false;
  private collection: MapFeatureCollection = { type: "FeatureCollection", features: [] };

  public constructor(
    element: HTMLElement,
    center: CanonicalPoint,
    options: GoogleNetworkMapOptions = {},
  ) {
    const maps = mapsNamespace();
    if (maps === null) {
      throw new Error("Load the map script before creating a map adapter.");
    }
    this.maps = maps;
    this.map = new maps.Map(element, {
      center: toMapLatLng(center),
      fullscreenControl: true,
      mapTypeControl: true,
      mapTypeControlOptions: { mapTypeIds: ["terrain", "roadmap", "hybrid"] },
      mapTypeId: "terrain",
      streetViewControl: false,
      zoom: options.zoom ?? 13,
    });
    this.routeCasing = new maps.Data({
      controls: [],
      drawingMode: null,
      map: this.map,
    });
    this.routeCasing.setStyle((feature) => {
      const foreground = styleForFeature(maps, feature);
      return {
        clickable: false,
        strokeColor: "#ffffff",
        strokeOpacity: 1,
        strokeWeight: (foreground.strokeWeight ?? 4) + 4,
        zIndex: (foreground.zIndex ?? 20) - 1,
      };
    });
    this.dataLayer = new maps.Data({
      controls: [],
      drawingMode: null,
      map: this.map,
    });
    this.dataLayer.setStyle((feature) => styleForFeature(maps, feature));
    this.clickListener =
      options.onMapClick === undefined
        ? null
        : this.map.addListener("click", (event) => {
            const latitude = event.latLng?.lat();
            const longitude = event.latLng?.lng();
            if (
              latitude === undefined ||
              longitude === undefined ||
              !Number.isFinite(latitude) ||
              !Number.isFinite(longitude) ||
              latitude < -90 ||
              latitude > 90 ||
              longitude < -180 ||
              longitude > 180
            ) {
              return;
            }
            options.onMapClick?.(
              fromMapLatLng({ lat: latitude, lng: longitude }),
            );
          });
  }

  public dispose(): void {
    this.clickListener?.remove();
    this.dataLayer.setMap(null);
    this.routeCasing.setMap(null);
  }

  public renderProject(document: ProjectDocument): void {
    for (const layer of [this.routeCasing, this.dataLayer]) {
      const previous: unknown[] = [];
      layer.forEach((feature) => previous.push(feature));
      previous.forEach((feature) => layer.remove(feature));
    }
    this.collection = projectToMapGeoJson(document);
    // Both strokes are native geographic features. Google owns their projection
    // during zoom/pan; fixed pixel widths keep routes legible at distant zooms.
    this.routeCasing.addGeoJson({
      type: "FeatureCollection",
      features: this.collection.features.filter((feature) => feature.geometry.type === "LineString"),
    });
    this.dataLayer.addGeoJson(this.collection);
    if (this.projectId !== document.id) {
      this.projectId = document.id;
      this.hasFittedGeometry = false;
    }
    // Saving edits must not reset the view a user has deliberately panned/zoomed.
    if (!this.hasFittedGeometry) this.fitProject();
  }

  public fitProject(): void {
    const coordinates = this.collection.features.flatMap((feature) =>
      pointsInGeometry(feature.geometry),
    );
    if (coordinates.length === 0) return;
    const bounds = new this.maps.LatLngBounds();
    coordinates.forEach(([longitude, latitude]) => {
      bounds.extend({ lat: latitude, lng: longitude });
    });
    this.map.fitBounds(bounds, 36);
    this.hasFittedGeometry = true;
  }
}
